/**
 * AbilityGroundsLabPanel — deterministic, integrated ability visual lab.
 *
 * Modernized tabbed panel container hosting Cast Bench, Capture Automation,
 * Scenarios & Stress, and Render Telemetry tabs under 500 LOC.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAbilitiesForClass } from '../../../../shared/content/AbilityDataAdapter';
import {
  ABILITY_GROUNDS_PREPARED_MESSAGE,
  ABILITY_GROUNDS_PREPARE_MESSAGE,
  ABILITY_GROUNDS_PARK_X,
  ABILITY_GROUNDS_TARGETS,
  type AbilityGroundsPrepareResult,
} from '../../../../shared/qa/AbilityGroundsLab';
import { ServiceRegistry } from '../../../core/ServiceRegistry';
import { PlayerServices } from '../../../core/Services';
import { EventBus } from '../../../../shared/EventBus';
import { VisualLifecycleStore } from '../../../diagnostics/VisualLifecycleStore';
import {
  latestAbilityCaptureStatus,
  subscribeAbilityCaptureStatus,
  waitForAbilityCapture,
  type AbilityCaptureStatus,
} from '../../../diagnostics/AbilityCaptureStatus';
import { Badge, TabBar, type TabDescriptor } from '../../primitives';
import {
  COLORS,
  FONTS,
  FONT_SIZE,
  FONT_WEIGHT,
  SPACE,
  formatCallingName,
} from '../../theme';
import {
  CLASS_FALLBACK,
  GROUNDS_PREPARE_TIMEOUT_MS,
  type CaptureSweepState,
  type H2kProjectileRow,
  type H2kSnapshot,
  type LabState,
  readSnapshot,
  roomOrNull,
  selectGroundsTarget,
  statusTone,
} from './types';
import { AbilityCastBenchTab } from './AbilityCastBenchTab';
import { AbilityCaptureTab } from './AbilityCaptureTab';
import { AbilityScenariosTab } from './AbilityScenariosTab';
import { AbilityTelemetryTab } from './AbilityTelemetryTab';
import { bootSearchParams } from '../../../../config/boot/bootUrl';

export function prepareGroundsView(targetId: string): Promise<AbilityGroundsPrepareResult> {
  const room = roomOrNull();
  if (!room) return Promise.reject(new Error('No live room is connected'));

  return new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe = () => {};
    const timeoutRef: { id?: number } = {};
    const finish = (result?: AbilityGroundsPrepareResult, error?: Error) => {
      if (settled) return;
      settled = true;
      if (timeoutRef.id !== undefined) window.clearTimeout(timeoutRef.id);
      unsubscribe();
      if (error) reject(error);
      else if (result) resolve(result);
    };
    unsubscribe = EventBus.getInstance().onDynamic(
      `net:message:${ABILITY_GROUNDS_PREPARED_MESSAGE}`,
      (message: AbilityGroundsPrepareResult) => {
        const result = message as AbilityGroundsPrepareResult;
        if (result.ok && result.targetId !== targetId) return;
        if (!result.ok) finish(undefined, new Error(result.code ?? 'server rejected the focused view'));
        else {
          const waitForReplica = () => {
            if (settled) return;
            const npcs = room.state?.npcs;
            const replicated = ABILITY_GROUNDS_TARGETS.every((definition, index) => {
              const npc = npcs?.get?.(definition.id);
              const expectedX = definition.id === targetId
                ? definition.x
                : ABILITY_GROUNDS_PARK_X - (index * 80);
              return npc && Number(npc.x) === expectedX;
            });
            if (replicated) finish(result);
            else window.requestAnimationFrame(waitForReplica);
          };
          waitForReplica();
        }
      },
    );
    timeoutRef.id = window.setTimeout(
      () => finish(undefined, new Error(`target staging timed out after ${GROUNDS_PREPARE_TIMEOUT_MS}ms`)),
      GROUNDS_PREPARE_TIMEOUT_MS,
    );
    room.send(ABILITY_GROUNDS_PREPARE_MESSAGE, { targetId });
  });
}

const LAB_TABS: TabDescriptor[] = [
  { id: 'bench', label: 'Cast Bench', icon: '✦' },
  { id: 'capture', label: 'Capture Suite', icon: '🎞' },
  { id: 'scenarios', label: 'Scenarios', icon: '⚙' },
  { id: 'telemetry', label: 'Telemetry', icon: '◈' },
];

export const AbilityGroundsLabPanelContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState('bench');
  const [snap, setSnap] = useState<H2kSnapshot | null>(null);
  const [lastProjectile, setLastProjectile] = useState<H2kProjectileRow | null>(null);
  const [labState, setLabState] = useState<LabState>('connecting');
  const [labResult, setLabResult] = useState<AbilityGroundsPrepareResult | null>(null);
  const [selectedAbility, setSelectedAbility] = useState('');
  const [selectedTarget, setSelectedTarget] = useState<string>(ABILITY_GROUNDS_TARGETS[0].id);
  const [scenarioCount, setScenarioCount] = useState(1);
  const [lastAction, setLastAction] = useState('Waiting for the server lab handshake…');
  const [captureReceipt, setCaptureReceipt] = useState<AbilityCaptureStatus | null>(() => latestAbilityCaptureStatus());
  const [sweep, setSweep] = useState<CaptureSweepState>({
    running: false, completed: 0, total: 0, current: null, castIds: [], error: null,
  });
  const sweepCancelRef = useRef(false);

  useEffect(() => subscribeAbilityCaptureStatus(setCaptureReceipt), []);

  useEffect(() => {
    const poll = () => {
      const next = readSnapshot();
      setSnap(next);
      const newest = next?.projectiles?.[next.projectiles.length - 1];
      if (newest) setLastProjectile(newest);
    };
    poll();
    const id = window.setInterval(poll, 125);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const pinLatestProjectileSpawn = () => {
      const localSessionId = readSnapshot()?.localSessionId;
      const events = VisualLifecycleStore.latest(40);
      for (let index = events.length - 1; index >= 0; index -= 1) {
        const event = events[index];
        if (event.type !== 'projectile_spawn' || !event.abilityId) continue;
        const ownerId = typeof event.meta?.ownerId === 'string' ? event.meta.ownerId : null;
        if (localSessionId && ownerId && ownerId !== localSessionId) continue;
        setLastProjectile({
          abilityId: event.abilityId,
          width: Number(event.meta?.width ?? 0),
          height: Number(event.meta?.height ?? 0),
          shape: typeof event.meta?.shape === 'string' ? event.meta.shape : null,
          ownerId,
        });
        return;
      }
    };

    pinLatestProjectileSpawn();
    return VisualLifecycleStore.subscribe(pinLatestProjectileSpawn);
  }, []);

  useEffect(() => {
    let disposed = false;
    let requestedSessionId: string | null = null;

    const unsubscribe = EventBus.getInstance().onDynamic(
      `net:message:${ABILITY_GROUNDS_PREPARED_MESSAGE}`,
      (message: AbilityGroundsPrepareResult) => {
        if (disposed) return;
        const result = message as AbilityGroundsPrepareResult;
        setLabResult(result);
        if (!result?.ok) {
          setLabState('error');
          setLastAction(`Lab unavailable: ${result?.code ?? 'unknown server response'}`);
          return;
        }
        setLabState('ready');
        setSelectedTarget(result.targetId ?? ABILITY_GROUNDS_TARGETS[0].id);
        selectGroundsTarget(result.targetId ?? ABILITY_GROUNDS_TARGETS[0].id);
        const preparedTarget = ABILITY_GROUNDS_TARGETS.find((target) => target.id === result.targetId);
        setLastAction(`Ready · ${result.abilityCount} ${result.heroClass ?? ''} abilities · ${preparedTarget?.label ?? 'Near'} target staged`);
      },
    );

    const connect = () => {
      let network: any;
      try {
        network = ServiceRegistry.getInstance().get('NetworkManager', { optional: true });
      } catch {
        return;
      }
      if (!network) return;

      const room = network.getRoom?.();
      if (!room || requestedSessionId === room.sessionId) return;
      requestedSessionId = room.sessionId;
      setLabState('preparing');
      room.send(ABILITY_GROUNDS_PREPARE_MESSAGE, {});
    };

    connect();
    const id = window.setInterval(connect, 250);
    return () => {
      disposed = true;
      window.clearInterval(id);
      unsubscribe();
    };
  }, []);

  const localClass = useMemo(() => {
    const renderedClass = snap?.nameplates?.find((nameplate) => nameplate.isLocal)?.class;
    return (labResult?.heroClass || renderedClass || CLASS_FALLBACK).toLowerCase();
  }, [labResult?.heroClass, snap]);

  const abilities = useMemo(() => {
    try {
      return Object.values(getAbilitiesForClass(localClass)) as Array<{ id: string; name: string; target?: string }>;
    } catch {
      return [];
    }
  }, [localClass]);

  const appliedUrlAbilityRef = useRef<string | null>(null);

  useEffect(() => {
    const urlAbility = typeof window !== 'undefined'
      ? bootSearchParams().get('ability')
      : null;
    if (urlAbility && appliedUrlAbilityRef.current !== urlAbility && abilities.some((ability) => ability.id === urlAbility)) {
      appliedUrlAbilityRef.current = urlAbility;
      setSelectedAbility(urlAbility);
      return;
    }
    if (!abilities.some((ability) => ability.id === selectedAbility)) {
      setSelectedAbility(abilities[0]?.id ?? '');
    }
  }, [abilities, selectedAbility]);

  const resetLab = () => {
    const room = roomOrNull();
    if (!room) {
      setLabState('error');
      setLastAction('No live room is connected.');
      return;
    }
    setLabState('preparing');
    setLastAction('Restoring spawn, vitals, cooldowns, and authored dummies…');
    room.send(ABILITY_GROUNDS_PREPARE_MESSAGE, {});
  };

  const selectedAbilityData = abilities.find((ability) => ability.id === selectedAbility);
  const sweepTargets = selectedAbilityData?.target === 'self' || selectedAbilityData?.target === 'friendly'
    ? [ABILITY_GROUNDS_TARGETS[0]]
    : [...ABILITY_GROUNDS_TARGETS];

  const sweepVisuals = () => {
    try {
      const vfx = ServiceRegistry.getInstance().get('VFXSystem', { optional: true }) as { sweepTransient?: (reason: string) => void } | null;
      vfx?.sweepTransient?.('ability-grounds-capture-view');
    } catch { /* best-effort cleanup */ }
  };

  const runCaptureSweep = async () => {
    if (sweep.running) {
      sweepCancelRef.current = true;
      setLastAction('Stopping after the current capture finishes…');
      return;
    }
    if (labState !== 'ready' || !selectedAbility) return;
    const manager = PlayerServices.manager();
    if (!manager) {
      setLastAction('Player manager is not ready yet.');
      return;
    }

    const abilityId = selectedAbility;
    const abilityName = selectedAbilityData?.name ?? abilityId;
    sweepCancelRef.current = false;
    setSweep({ running: true, completed: 0, total: sweepTargets.length, current: null, castIds: [], error: null });
    setLastAction(`Capture sweep started · ${abilityName} · ${sweepTargets.length} deterministic view${sweepTargets.length === 1 ? '' : 's'}`);

    const castIds: string[] = [];
    let completed = 0;
    try {
      for (const definition of sweepTargets) {
        if (sweepCancelRef.current) break;
        setSelectedTarget(definition.id);
        setSweep({ running: true, completed, total: sweepTargets.length, current: definition.label, castIds: [...castIds], error: null });
        setLastAction(`Staging ${definition.label} ${definition.distance}px · parking other dummies…`);
        await prepareGroundsView(definition.id);
        sweepVisuals();
        selectGroundsTarget(definition.id);
        setLastAction(`Capturing ${abilityName} · ${definition.label} ${definition.distance}px…`);

        const startedAfter = Date.now();
        const accepted = manager.performAbility(abilityId, { source: 'ability-grounds-lab' });
        if (!accepted) throw new Error(`${definition.label} cast was rejected`);
        const receipt = await waitForAbilityCapture(abilityId, definition.id, startedAfter);
        castIds.push(receipt.castId);
        completed += 1;
        setSweep({ running: true, completed, total: sweepTargets.length, current: definition.label, castIds: [...castIds], error: null });
      }

      const stopped = sweepCancelRef.current;
      setLastAction(stopped
        ? `Capture sweep stopped · ${completed}/${sweepTargets.length} views stored`
        : `Capture sweep complete · ${completed}/${sweepTargets.length} views · open montage review`);
      setSweep({ running: false, completed, total: sweepTargets.length, current: null, castIds, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setLastAction(`Capture sweep failed · ${message}`);
      setSweep({ running: false, completed, total: sweepTargets.length, current: null, castIds, error: message });
    }
  };

  const reviewCaptures = () => {
    const query = new URLSearchParams({ cls: localClass, ability: selectedAbility });
    window.location.assign(`/api/diag/ability-captures/view?${query.toString()}`);
  };

  const localProjectiles = (snap?.projectiles ?? []).filter(
    (projectile) => projectile.ownerId != null && projectile.ownerId === snap?.localSessionId,
  );

  return (
    <div style={panel} data-testid="ability-grounds-lab">
      <div style={heroRow}>
        <div>
          <div style={eyebrow}>🎯 ABILITY VISUAL LAB</div>
          <div style={heroTitle}>{formatCallingName(localClass)}</div>
        </div>
        <div style={badgeRow}>
          <Badge tone={statusTone(labState)} size="md">
            {labState === 'ready' ? '● READY' : labState.toUpperCase()}
          </Badge>
        </div>
      </div>

      <div style={{ marginBottom: SPACE.xs }}>
        <TabBar
          tabs={LAB_TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          variant="pills"
        />
      </div>

      {activeTab === 'bench' && (
        <AbilityCastBenchTab
          labState={labState}
          setLabState={setLabState}
          abilities={abilities}
          selectedAbility={selectedAbility}
          setSelectedAbility={setSelectedAbility}
          selectedTarget={selectedTarget}
          setSelectedTarget={setSelectedTarget}
          lastAction={lastAction}
          setLastAction={setLastAction}
          resetLab={resetLab}
          sweepRunning={sweep.running}
        />
      )}

      {activeTab === 'capture' && (
        <AbilityCaptureTab
          labState={labState}
          selectedAbility={selectedAbility}
          selectedAbilityName={selectedAbilityData?.name}
          sweep={sweep}
          sweepTargetsCount={sweepTargets.length}
          captureReceipt={captureReceipt}
          runCaptureSweep={runCaptureSweep}
          reviewCaptures={reviewCaptures}
        />
      )}

      {activeTab === 'scenarios' && (
        <AbilityScenariosTab
          scenarioCount={scenarioCount}
          setScenarioCount={setScenarioCount}
          setLastAction={setLastAction}
        />
      )}

      {activeTab === 'telemetry' && (
        <AbilityTelemetryTab
          snap={snap}
          lastProjectile={lastProjectile}
          localProjectilesCount={localProjectiles.length}
        />
      )}
    </div>
  );
};

const panel: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: SPACE.sm,
  height: '100%',
  overflowY: 'auto',
  padding: SPACE.md,
  color: COLORS.textPrimary,
  fontFamily: FONTS.ui,
};

const heroRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: SPACE.md,
};

const eyebrow: React.CSSProperties = {
  color: COLORS.brandAccent,
  fontSize: FONT_SIZE.xs,
  fontWeight: FONT_WEIGHT.bold,
  letterSpacing: '0.14em',
};

const heroTitle: React.CSSProperties = {
  fontSize: FONT_SIZE.xxl,
  fontWeight: FONT_WEIGHT.bold,
  textTransform: 'capitalize',
  marginTop: SPACE.xxs,
};

const badgeRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.xs,
};
